import { useState } from "react";
import { useEditor } from "prosekit/react";
import {
  AutocompleteEmpty,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePopover,
} from "prosekit/react/autocomplete";

import type { EditorExtension } from "./extension";
import { stringify } from "query-string";
import { useQuery } from "react-query";
import { DirectoryMembership } from "../../directory/types";

// Trigger/query regex for the "@" member mention autocomplete.
//
// `\w` matches ASCII only in JS (even with the `u` flag), so the previous
// `/(?<=^|\s)@\w*$/` failed to match as soon as an umlaut (ä/ö/ü/ß) was typed,
// which made ProseKit close the popover and never send the query to the backend
// (tixxt/core#64). `\p{L}` covers umlauts and other letters, `\p{M}` handles
// decomposed umlauts (letter + combining diaeresis), `\p{N}`/`_` keep parity
// with `\w`. Single spaces between words are allowed so "Vorname Nachname" can be
// searched as a whole, bounded to at most 4 tokens so the popover doesn't follow
// a whole sentence after "@word ".
export const MENTION_TRIGGER_REGEX =
  /(?<=^|\s)@(?:[\p{L}\p{M}\p{N}_]+(?: [\p{L}\p{M}\p{N}_]+){0,3})?$/u;

export default function MentionSelect() {
  const editor = useEditor<EditorExtension>();
  const [q, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const handleMemberInsert = (id: string, name: string) => {
    editor.commands.insertMention({
      id: `@[${id}:${name}]`,
      value: `@${name}`,
      kind: "member",
    });
    editor.commands.insertText({ text: " " });
  };

  const { isLoading, data: members } = useQuery<DirectoryMembership[]>(
    `/members?${stringify({ q })}`,
    {
      enabled: open,
    },
  );

  return (
    <AutocompletePopover
      regex={MENTION_TRIGGER_REGEX}
      onQueryChange={setQuery}
      onOpenChange={setOpen}
      className="relative block max-h-[25rem] min-w-[15rem] select-none overflow-auto whitespace-nowrap p-1 z-10 box-border rounded-lg border border-gray-200 bg-white shadow-lg [&:not([data-state])]:hidden"
    >
      <AutocompleteList filter={null}>
        <AutocompleteEmpty className="relative flex items-center justify-between min-w-[8rem] scroll-my-1 rounded px-3 py-1.5 box-border cursor-default select-none whitespace-nowrap outline-hidden data-focused:bg-gray-100">
          {isLoading
            ? I18n.t("js.composer.editor.buttons.loading")
            : I18n.t("js.composer.editor.buttons.no_result")}
        </AutocompleteEmpty>

        {members?.map((member) => (
          <AutocompleteItem
            key={member.id}
            className="relative flex items-center justify-between min-w-[8rem] scroll-my-1 rounded px-2 py-1.5 box-border cursor-default select-none whitespace-nowrap outline-hidden data-focused:bg-gray-100"
            onSelect={() => handleMemberInsert(member.id, member.displayName)}
          >
            <img
              src={member.images.medium}
              alt={""}
              aria-hidden
              className={"w-6 h-6 mr-2 rounded-full"}
            />
            <span className={`grow ${isLoading ? "opacity-50" : undefined}`}>
              {member.displayName}
            </span>
          </AutocompleteItem>
        ))}
      </AutocompleteList>
    </AutocompletePopover>
  );
}
